import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useLanguage } from '../context/LanguageContext';
import {
  AlertCircle, AlertTriangle, CheckCircle2, Eye, HelpCircle, Mail,
  Phone, RefreshCw, Target, Wrench, XCircle,
} from 'lucide-react';

interface ServiceHealth {
  id: string;
  name: string;
  url: string;
  isPublic: boolean;
  message: string;
  forcedStatus: string;
  status: 'operational' | 'degraded' | 'down' | 'unknown';
  httpStatus: number | null;
  latency: number | null;
  checkedAt: number | null;
  detail: 'ok' | 'http' | 'timeout' | 'network' | 'forced' | null;
  uptime24h: number | null;
  uptime1h: number | null;
}

interface HealthPayload {
  checkedAt: number;
  services: ServiceHealth[];
}

const REFRESH_MS = 30000;

// ─── Bilingual message helpers ──────────────────────────────────────────────

/**
 * A stored message may be a plain string (legacy) or a JSON object
 * { en: "...", fr: "..." }. This helper returns the right variant.
 */
function parseCustomMessage(raw: string, lang: 'en' | 'fr'): string {
  if (!raw) return '';
  try {
    const p = JSON.parse(raw);
    if (p && typeof p === 'object' && (p.en || p.fr)) {
      return lang === 'fr' ? (p.fr || p.en || '') : (p.en || p.fr || '');
    }
  } catch {}
  return raw; // plain text — backward compatible
}

/**
 * Option D: shown when a status is set but no custom message exists yet.
 * These fire immediately on detection, before team awareness is confirmed.
 */
const DEFAULT_MESSAGES: Record<string, { en: string; fr: string }> = {
  investigating:  {
    en: 'Automated monitoring has detected an issue. Assessment is underway.',
    fr: 'La surveillance automatisée a détecté un problème. L\'évaluation est en cours.',
  },
  identified:     {
    en: 'The root cause has been identified. A fix is being implemented.',
    fr: 'La cause principale a été identifiée. Un correctif est en cours de déploiement.',
  },
  monitoring:     {
    en: 'A fix has been deployed. We are monitoring the situation closely.',
    fr: 'Un correctif a été déployé. Nous surveillons la situation de près.',
  },
  degraded:       {
    en: 'Some services are experiencing degraded performance. Our team is working on a resolution.',
    fr: 'Certains services subissent des performances dégradées. Notre équipe travaille sur une résolution.',
  },
  partial_outage: {
    en: 'Some components are currently unavailable. We are investigating and will provide updates.',
    fr: 'Certains composants sont actuellement indisponibles. Nous enquêtons et fournirons des mises à jour.',
  },
  outage:         {
    en: 'We are experiencing a major service outage. We will update every 30 minutes.',
    fr: 'Nous subissons une panne de service majeure. Nous vous tiendrons informés toutes les 30 minutes.',
  },
  maintenance:    {
    en: 'Scheduled maintenance is in progress. Services may be intermittently unavailable.',
    fr: 'Une maintenance planifiée est en cours. Les services peuvent être intermittents.',
  },
  upgrade:        {
    en: 'A system upgrade is underway. Some features may be temporarily unavailable.',
    fr: 'Une mise à niveau du système est en cours. Certaines fonctionnalités peuvent être temporairement indisponibles.',
  },
  unavailable:    {
    en: 'Automated monitoring has detected an issue. Assessment is underway.',
    fr: 'La surveillance automatisée a détecté un problème. L\'évaluation est en cours.',
  },
};

const FALLBACK_MSG = {
  en: 'Automated monitoring has detected an issue. Assessment is underway.',
  fr: 'La surveillance automatisée a détecté un problème. L\'évaluation est en cours.',
};

function formatTimeAgo(ts: number | null, lang: 'fr' | 'en'): string {
  if (!ts) return lang === 'fr' ? 'jamais' : 'never';
  const diff = Math.max(0, Date.now() - ts);
  const s = Math.floor(diff / 1000);
  if (s < 60) return lang === 'fr' ? `il y a ${s}s` : `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return lang === 'fr' ? `il y a ${m} min` : `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return lang === 'fr' ? `il y a ${h}h` : `${h}h ago`;
  const d = Math.floor(h / 24);
  return lang === 'fr' ? `il y a ${d}j` : `${d}d ago`;
}

export default function StatusPage() {
  const { lang, setLang, t } = useLanguage();

  const [data, setData] = useState<HealthPayload | null>(null);
  const [globalStatus, setGlobalStatus] = useState<{ status: string; customMessage: string }>({ status: 'operational', customMessage: '' });
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [lastFetched, setLastFetched] = useState<number | null>(null);

  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const fetchAll = async () => {
    setRefreshing(true);
    try {
      const [healthRes, statusRes] = await Promise.all([
        fetch('/api/status-page/health', { cache: 'no-store' }),
        fetch('/api/status-page/status', { cache: 'no-store' })
      ]);
      if (healthRes.ok) setData(await healthRes.json());
      if (statusRes.ok) setGlobalStatus(await statusRes.json());
      setLastFetched(Date.now());
    } catch {}
    finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchAll();
    timerRef.current = setInterval(fetchAll, REFRESH_MS);
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, []);


  // Compute overall state from services + global override
  const overall = useMemo(() => {
    const services = data?.services || [];
    const globalOverride = ['maintenance', 'upgrade', 'outage', 'degraded', 'unavailable', 'investigating', 'partial_outage'].includes(globalStatus.status);

    let level: 'ok' | 'degraded' | 'down' | 'maintenance' = 'ok';
    if (globalOverride) {
      if (['maintenance', 'upgrade'].includes(globalStatus.status)) level = 'maintenance';
      else if (['degraded', 'partial_outage'].includes(globalStatus.status)) level = 'degraded';
      else level = 'down'; // outage, unavailable, investigating
    } else if (services.length > 0) {
      if (services.some(s => s.status === 'down')) level = 'down';
      else if (services.some(s => s.status === 'degraded' || s.status === 'unknown')) level = 'degraded';
    }

    const titles = {
      fr: {
        ok: 'Tous les services sont opérationnels',
        degraded: 'Performance dégradée',
        down: 'Incident en cours',
        maintenance: 'Maintenance planifiée'
      },
      en: {
        ok: 'All systems operational',
        degraded: 'Partial degradation',
        down: 'Active incident',
        maintenance: 'Scheduled maintenance'
      }
    };
    return { level, title: titles[lang][level] };
  }, [data, globalStatus, lang]);

  const services = data?.services || [];

  return (
    <div className="page">
      <header className="sp-header">
        <div className="sp-brand">
          <img src="/logo.png" alt="UDesign" className="sp-logo" />
          <div>
            <div className="sp-brand-name">UDesign</div>
            <div className="sp-brand-sub">{t('motto')}</div>
          </div>
        </div>
        <div className="sp-header-right">
          <div className="lang-switch">
            <button
              type="button"
              className={`lang ${lang === 'fr' ? 'active' : ''}`}
              aria-label="Français"
              aria-pressed={lang === 'fr'}
              onClick={() => setLang('fr')}
            >FR</button>
            <button
              type="button"
              className={`lang ${lang === 'en' ? 'active' : ''}`}
              aria-label="English"
              aria-pressed={lang === 'en'}
              onClick={() => setLang('en')}
            >EN</button>
          </div>
        </div>
      </header>

      <section className={`sp-banner sp-banner-${overall.level}`} aria-live="polite">
        <div className="sp-banner-icon" aria-hidden="true">
          {overall.level === 'ok' && <CheckCircle2 size={20} strokeWidth={2} />}
          {overall.level === 'degraded' && <AlertTriangle size={20} strokeWidth={2} />}
          {overall.level === 'down' && <XCircle size={20} strokeWidth={2} />}
          {overall.level === 'maintenance' && <Wrench size={20} strokeWidth={2} />}
        </div>
        <div className="sp-banner-body">
          <h1 className="sp-banner-title">{overall.title}</h1>
          {(() => {
            const custom = parseCustomMessage(globalStatus.customMessage, lang);
            if (custom) return <p className="sp-banner-msg">{custom}</p>;
            if (overall.level !== 'ok') {
              const msg = DEFAULT_MESSAGES[globalStatus.status] ?? FALLBACK_MSG;
              return <p className="sp-banner-msg">{lang === 'fr' ? msg.fr : msg.en}</p>;
            }
            return null;
          })()}
        </div>
      </section>

      {overall.level !== 'ok' && (
        <div className="sp-contact-row">
          <a className="sp-link-btn" href="tel:+18194811022"><Phone size={16} aria-hidden="true" />819 481 1022</a>
          <a className="sp-link-btn" href="mailto:contact@udesignproductions.com"><Mail size={16} aria-hidden="true" />contact@udesignproductions.com</a>
        </div>
      )}

      <section className="sp-panel">
        <div className="sp-panel-head">
          <div>
            <h2 className="sp-panel-title">{lang === 'fr' ? 'Services' : 'Components'}</h2>
          </div>
          <div className="sp-panel-controls">
            <button type="button" className="sp-btn-ghost" onClick={fetchAll} disabled={refreshing}>
              <RefreshCw size={15} className={refreshing ? 'is-spinning' : ''} aria-hidden="true" />
              {refreshing
                ? (lang === 'fr' ? 'Actualisation…' : 'Refreshing…')
                : (lang === 'fr' ? 'Actualiser' : 'Refresh')}
            </button>
          </div>
        </div>

        <ul className="sp-svc-list">
          {loading && services.length === 0 && (
            <>
              <li className="sp-svc sp-svc-skel" />
              <li className="sp-svc sp-svc-skel" />
              <li className="sp-svc sp-svc-skel" />
            </>
          )}
          {!loading && services.length === 0 && (
            <li className="sp-empty">
              {lang === 'fr' ? 'Aucun service configuré.' : 'No services configured.'}
            </li>
          )}
          {services.map(svc => (
            <li key={svc.id} className="sp-svc">
              <div className="sp-svc-main">
                <div className="sp-svc-name">{svc.name}</div>
                <div className="sp-svc-meta">
                  <span className="sp-svc-host">{new URL(svc.url).hostname}</span>
                  {svc.latency != null && (
                    <span className="sp-svc-meta-sep">·</span>
                  )}
                  {svc.latency != null && (
                    <span>{svc.latency} ms</span>
                  )}
                  {svc.uptime24h != null && (
                    <>
                      <span className="sp-svc-meta-sep">·</span>
                      <span>{svc.uptime24h}% {lang === 'fr' ? '24h' : '24h'}</span>
                    </>
                  )}
                  {svc.checkedAt && (
                    <>
                      <span className="sp-svc-meta-sep">·</span>
                      <span>{formatTimeAgo(svc.checkedAt, lang)}</span>
                    </>
                  )}
                </div>
                {svc.message && (
                  <div className="sp-svc-msg">
                    {parseCustomMessage(svc.message, lang) || svc.message}
                  </div>
                )}
              </div>
              <StatusPill status={svc.status} forcedStatus={svc.forcedStatus} lang={lang} />
            </li>
          ))}
        </ul>

        <div className="sp-footer">
          <span>
            {lang === 'fr' ? 'Dernière actualisation' : 'Last refreshed'}:{' '}
            <strong>{formatTimeAgo(lastFetched, lang)}</strong>
          </span>
          <span className="sp-footer-sep">·</span>
          <span>{lang === 'fr' ? 'Mise à jour auto toutes les 30 s' : 'Auto-refresh every 30 s'}</span>
        </div>
      </section>

      <footer className="sp-page-footer">
        <span>© {new Date().getFullYear()} UDesign Productions</span>
        <span className="sp-footer-sep">·</span>
        <a href="https://udesignproductions.com">udesignproductions.com</a>
      </footer>
    </div>
  );
}

type LucideIcon = React.ComponentType<{ size?: number | string; strokeWidth?: number | string; className?: string }>;

const FORCED_PILL: Record<string, { en: string; fr: string; cls: 'operational' | 'degraded' | 'down'; Icon: LucideIcon }> = {
  investigating: { en: 'Investigating',     fr: 'Investigation',    cls: 'down',     Icon: AlertCircle },
  identified:    { en: 'Identified',        fr: 'Identifié',        cls: 'down',     Icon: Target },
  monitoring:    { en: 'Monitoring',        fr: 'Surveillance',     cls: 'degraded', Icon: Eye },
  partial_outage:{ en: 'Partial Outage',    fr: 'Panne partielle',  cls: 'degraded', Icon: AlertTriangle },
  maintenance:   { en: 'Under Maintenance', fr: 'Maintenance',      cls: 'degraded', Icon: Wrench },
};

const STATUS_ICONS: Record<string, LucideIcon> = {
  operational: CheckCircle2,
  degraded:    AlertTriangle,
  down:        XCircle,
  unknown:     HelpCircle,
};

function StatusPill({ status, forcedStatus, lang }: {
  status: ServiceHealth['status'];
  forcedStatus: string;
  lang: 'fr' | 'en';
}) {
  const forced = FORCED_PILL[forcedStatus];
  if (forced) {
    const { Icon } = forced;
    return (
      <span className={`sp-pill sp-pill-${forced.cls}`}>
        <Icon size={12} strokeWidth={2.5} />
        {lang === 'en' ? forced.en : forced.fr}
      </span>
    );
  }
  const labels = {
    fr: { operational: 'Opérationnel', degraded: 'Dégradé', down: 'Hors service', unknown: 'Inconnu' },
    en: { operational: 'Operational',  degraded: 'Degraded', down: 'Down',         unknown: 'Unknown' }
  };
  const Icon = STATUS_ICONS[status] || HelpCircle;
  return (
    <span className={`sp-pill sp-pill-${status}`}>
      <Icon size={12} strokeWidth={2.5} />
      {labels[lang][status]}
    </span>
  );
}
